<template>
    <bs5-base-modal title="Client Licenses" :visible="visible" width="80%" @close="closeModal">
        <template v-slot:default>
            <div v-if="loading" class="text-center">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>

            <div v-else-if="licenses.length === 0" class="text-center">
                <p>No licenses found.</p>
            </div>

            <div v-else>
                <div class="table-responsive">
                    <table class="table table-striped table-hover">
                        <thead class="table-dark">
                            <tr>
                                <th>#</th>
                                <th>Issued Date</th>
                                <th>Expire Date</th>
                                <th>Status</th>
                                <th>License Key</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(license, index) in licenses" :key="license.id">
                                <td>{{ index + 1 }}</td>
                                <td>{{ formatDate(license.issuedDate) }}</td>
                                <td>{{ license.expireDate ? formatDate(license.expireDate) : "Never" }}</td>
                                <td>
                                    <span class="badge" :class="license.isActive ? 'bg-success' : 'bg-danger'">
                                        {{ license.isActive ? "Active" : "Inactive" }}
                                    </span>
                                </td>
                                <td>
                                    <span class="badge bg-secondary">{{ license.licenseKey }}</span>
                                </td>
                                <td>
                                    <bs5-group-button>
                                        <bs5-save-button variant="info" v-if="license.isActive"
                                            @click="downloadLicense(clientId, license.id)">Download</bs5-save-button>
                                        <bs5-delete-button v-if="license.isActive"
                                            @click="deleteLicense(clientId, license.id)">Delete</bs5-delete-button>
                                    </bs5-group-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>

        <template v-slot:footer>
            <bs5-cancel-button @click="closeModal">Close</bs5-cancel-button>
        </template>
    </bs5-base-modal>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import licenseService from "@/services/license.service";
import { License } from "@/models/license.model";
import { showToast } from "@/utils/toast";

export default defineComponent({
    name: "ClientLicensesModal",
    props: {
        visible: { type: Boolean, required: true },
        clientId: { type: String, required: true },
    },
    emits: ["close"],
    data() {
        return {
            licenses: ref<License[]>([]),
            loading: ref(false),
        };
    },
    watch: {
        clientId: {
            immediate: true,
            handler(newId) {
                if (newId) this.fetchLicenses();
            },
        },
    },
    methods: {
        async fetchLicenses() {
            this.loading = true;
            try {
                this.licenses = await licenseService.getClientLicenses(this.clientId);
            } catch (error) {
                console.error("Error fetching licenses:", error);
            } finally {
                this.loading = false;
            }
        },
        formatDate(dateStr: string): string {
            return new Date(dateStr).toLocaleDateString();
        },
        closeModal() {
            this.$emit("close");
        },
        async downloadLicense(clientId: string, licenseId: string | null | undefined) {
            try {
                await licenseService.downloadLicense(clientId, licenseId as string);
                showToast("License downloaded!", "success");
            } catch (error) {
                showToast("Failed to download license!", "error");
            }
        },

        async deleteLicense(clientId: string, licenseId: string | null | undefined) {
            const response = await this.$swal.fire({
                title: "Are you sure?",
                text: "This action cannot be undone!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel",
            });

            if (response.isConfirmed) {
                try {
                    await licenseService.deleteLicense(clientId, licenseId as string);
                    showToast("The license has been removed.", "success");
                    this.fetchLicenses();
                    await this.fetchLicenses();
                } catch (error) {
                    showToast("Failed to delete license.", "error");
                }
            }
        }
    },
});
</script>